import { Button, Paper, Typography } from '@mui/material';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Overlay {
  /** Text shown on the left of the overlay. */
  message: ReactNode;
  /** Optional callback. When provided, a cancel button is rendered. */
  onCancel?: () => void;
  /** Optional override for the cancel button label. Defaults to `common:cancel`. */
  cancelLabel?: string;
}

export function useCanvasOverlay() {
  const { t } = useTranslation('common');
  const [overlay, setOverlay] = useState<Overlay | null>(null);

  const show = useCallback((next: Overlay) => {
    setOverlay(next);
  }, []);

  const hide = useCallback(() => {
    setOverlay(null);
  }, []);

  const handleCancel = useCallback(() => {
    overlay?.onCancel?.();
    setOverlay(null);
  }, [overlay]);

  const Overlay = useCallback(() => {
    if (overlay === null) return null;

    return (
      <Paper
        elevation={1}
        sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 4,
          width: 'min(42rem, calc(100% - 32px))',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderRadius: '4px',
        }}
      >
        <Typography fontSize={12} sx={{ '&:hover': { cursor: 'default' } }}>
          {overlay.message}
        </Typography>
        {overlay.onCancel && (
          <Button onClick={handleCancel} size='small'>
            {overlay.cancelLabel ?? t('common:cancel')}
          </Button>
        )}
      </Paper>
    );
  }, [overlay, handleCancel, t]);

  return useMemo(() => ({ show, hide, Overlay }), [show, hide, Overlay]);
}
